import { Box, Divider, Popover, Typography } from '@mui/material';
import PublishChecklistItem from './PublishChecklistItem';
import { Fragment, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../context/AppContext';
import { PRODUCT_TYPES } from '../../utils/constants';

const buildChecklistItems = (state, sslConnected) => {
  const { product, productType, businessInfo, paymentProviders } = state;

  const isDigitalProduct = productType === PRODUCT_TYPES.DIGITAL;

  const digitalFieldsComplete =
    product.digitalFileContentType &&
    product.digitalFileName &&
    product.digitalFileUrl &&
    product.internalFilename;

  const productComplete =
    product.name &&
    product.description &&
    product.imageUrl &&
    product.price > 0 &&
    product.currency &&
    (!isDigitalProduct || digitalFieldsComplete);

  const productStatus = productComplete
    ? 'complete'
    : !product.name && !product.description && !product.price
      ? 'incomplete'
      : 'in-progress';

  const businessComplete =
    businessInfo.companyName &&
    businessInfo.email &&
    businessInfo.address;

  const businessStatus = businessComplete
    ? 'complete'
    : !businessInfo.companyName && !businessInfo.email
      ? 'incomplete'
      : 'in-progress';

  const isStripeEnabled = Boolean(paymentProviders?.is_stripe_enabled);
  const isPayPalEnabled = Boolean(paymentProviders?.is_paypal_enabled);

  const hasPaymentMethod = isStripeEnabled || isPayPalEnabled;

  const paymentStatus = hasPaymentMethod ? 'complete' : 'incomplete';

  return [
    {
      title: 'Add Product',
      description:
        'You need to enter your product information before publishing',
      status: productStatus,
      to: '/checkout',
      itemLoading: (loaders) =>
        loaders.tokenLoading || loaders.checkoutStateLoading,
    },
    {
      title: 'Confirm Business Information',
      description:
        'Make sure you have confirmed the business information before publishing',
      status: businessStatus,
      to: '/order-confirmation',
      itemLoading: (loaders) =>
        loaders.tokenLoading ||
        loaders.checkoutStateLoading ||
        loaders.businessInfoLoading,
    },
    {
      title: 'Connect a Payment Method',
      description:
        'You must connect at least one payment method before publishing',
      status: paymentStatus,
      to: '/order-confirmation',
      itemLoading: (loaders) =>
        loaders.tokenLoading || loaders.paymentProvidersLoading,
    },
    {
      title: sslConnected
        ? 'SSL Connected'
        : 'Security Certificate is being installed',
      description:
        'Please wait while we complete the installation of your Security Certificate. Please check back in 1/2 an hour.',
      status: sslConnected ? 'complete' : 'incomplete',
      to: null,
      itemLoading: (loaders) =>
        loaders.tokenLoading || loaders.mfeAccountLoading,
    },
  ];
};

const ChecklistPopover = ({ open, anchorEl, handlePopoverClose }) => {
  const navigate = useNavigate();
  const {
    state,
    sslConnected,
    tokenLoading,
    checkoutStateLoading,
    businessInfoLoading,
    paymentProvidersLoading,
    mfeAccountLoading,
  } = useContext(AppContext);

  const loaders = {
    tokenLoading,
    checkoutStateLoading,
    businessInfoLoading,
    paymentProvidersLoading,
    mfeAccountLoading,
  };

  const checklistItems = buildChecklistItems(state, sslConnected);

  const handleItemClick = (item) => {
    if (!item.to || item.itemLoading(loaders)) return;
    navigate(item.to);
    handlePopoverClose();
  };

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (event) => {
      if (anchorEl?.contains(event.target)) return;
      const popoverEl = document.getElementById('mouse-over-popover');
      if (popoverEl?.contains(event.target)) return;
      handlePopoverClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open, anchorEl, handlePopoverClose]);

  return (
    <Popover
      id="mouse-over-popover"
      sx={{ pointerEvents: 'none', mt: 1, ml: -12 }}
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      onClose={handlePopoverClose}
      disableRestoreFocus
      slotProps={{
        paper: {
          sx: {
            width: '20rem',
            pointerEvents: 'auto',
          },
        },
      }}
    >
      <Box sx={{ background: '#EBEBEB80', pb: 1 }}>
        <Typography
          fontWeight="normal"
          variant="h6"
          sx={{ p: 2, pb: 1, color: '#212121' }}
        >
          Checklist
        </Typography>
        {checklistItems.map((item, index) => {
          const loading = item.itemLoading(loaders);
          const isLink = Boolean(item.to) && !loading;
          const content = (
            <PublishChecklistItem
              title={item.title}
              description={item.description}
              status={item.status}
              loading={loading}
            />
          );

          return (
            <Fragment key={item.title}>
              {isLink ? (
                <Box
                  component="button"
                  type="button"
                  onClick={() => handleItemClick(item)}
                  sx={{
                    display: 'block',
                    width: '100%',
                    border: 'none',
                    background: 'transparent',
                    padding: 0,
                    margin: 0,
                    textAlign: 'left',
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  {content}
                </Box>
              ) : (
                content
              )}
              {index < checklistItems.length - 1 && <Divider sx={{ my: 1 }} />}
            </Fragment>
          );
        })}
      </Box>
    </Popover>
  );
};

export default ChecklistPopover;
